/**
 * AdminOrderConsumptionInvoicing.jsx — Vague 5 (Admin)
 *
 * Section consolidée dans AdminOrderDetail :
 *   - KPIs reporting (sessions validées/pending/disputed, crédits coach, revenu plateforme)
 *   - Exports Excel / PDF
 *   - Grille consommations récentes + actions valider/contester/annuler
 *   - Génération facture (forfait OU consommation selon billing_mode)
 *   - Liste factures + actions émettre / marquer payée / annuler
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Activity, Download, FileText, CheckCircle2, AlertTriangle,
  XCircle, Euro, Clock, Loader2, Receipt, Plus, Printer, Ban,
  Send, Mail, X, Hourglass, Bot,
} from 'lucide-react';
import { getApiUrl } from '../../config/environment';

const API = getApiUrl();

// 🔒 Iter248 — Badge d'auto-validation avec countdown
// Affiché uniquement quand la deadline est armée (séance déjà consommée côté pipeline).
// L'admin garde un override "expert" replié par défaut.
function AutoValidationBadge({ deadlineIso }) {
  const [remaining, setRemaining] = React.useState(() => computeRemaining(deadlineIso));
  React.useEffect(() => {
    const t = setInterval(() => setRemaining(computeRemaining(deadlineIso)), 60_000);
    return () => clearInterval(t);
  }, [deadlineIso]);
  const expired = remaining.totalMs <= 0;
  return (
    <div className="inline-flex items-center gap-1.5 text-[10px] font-medium px-2 py-0.5 rounded-full"
         style={{ color: expired ? '#047857' : '#92400E', backgroundColor: expired ? '#D1FAE5' : '#FEF3C7' }}
         data-testid="auto-validation-badge"
         title="Validation automatique à l'expiration. Override admin disponible ci-dessous.">
      <Bot className="w-3 h-3" />
      {expired ? (
        <span>Validation auto imminente</span>
      ) : (
        <span>Auto-validation dans {remaining.label}</span>
      )}
    </div>
  );
}

function computeRemaining(iso) {
  const deadline = new Date(iso).getTime();
  const now = Date.now();
  const ms = deadline - now;
  if (ms <= 0) return { totalMs: 0, label: '0 min' };
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return { totalMs: ms, label: h >= 1 ? `${h}h${String(m).padStart(2, '0')}` : `${m} min` };
}

const CONS_STATUS_META = {
  pending_validation: { label: 'En attente', color: '#92400E', bg: '#FEF3C7' },
  validated: { label: 'Validée', color: '#047857', bg: '#D1FAE5' },
  disputed: { label: 'Contestée', color: '#991B1B', bg: '#FEE2E2' },
  cancelled: { label: 'Annulée', color: '#6B7280', bg: '#F3F4F6' },
};
const INV_STATUS_META = {
  draft: { label: 'Brouillon', color: '#6B7280', bg: '#F3F4F6' },
  issued: { label: 'Émise', color: '#1E40AF', bg: '#DBEAFE' },
  paid: { label: 'Payée', color: '#047857', bg: '#D1FAE5' },
  overdue: { label: 'En retard', color: '#991B1B', bg: '#FEE2E2' },
  void: { label: 'Annulée', color: '#6B7280', bg: '#F3F4F6' },
};

export default function AdminOrderConsumptionInvoicing({ orderId, order, token }) {
  const [report, setReport] = useState(null);
  const [consumptions, setConsumptions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [invoicingSummary, setInvoicingSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [sendModalInv, setSendModalInv] = useState(null);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rRep, rCons, rInv, rSum] = await Promise.all([
        fetch(`${API}/admin/enterprise/orders/${orderId}/report`, { headers }),
        fetch(`${API}/admin/enterprise/orders/${orderId}/consumption`, { headers }),
        fetch(`${API}/admin/enterprise/orders/${orderId}/invoices`, { headers }),
        fetch(`${API}/admin/enterprise/orders/${orderId}/invoicing-summary`, { headers }),
      ]);
      if (rRep.ok) setReport(await rRep.json());
      if (rCons.ok) setConsumptions((await rCons.json()).items || []);
      if (rInv.ok) setInvoices((await rInv.json()).items || []);
      if (rSum.ok) setInvoicingSummary(await rSum.json());
    } catch (e) {
      toast.error('Erreur chargement reporting');
    } finally {
      setLoading(false);
    }
  }, [orderId, token]); // eslint-disable-line

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const download = async (type) => {
    const res = await fetch(`${API}/admin/enterprise/orders/${orderId}/report/export/${type}`, { headers });
    if (!res.ok) { toast.error('Export impossible'); return; }
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `rapport_${order.quote_number}.${type === 'excel' ? 'xlsx' : 'pdf'}`;
    a.click();
  };

  const action = async (cid, verb, body = {}) => {
    const res = await fetch(`${API}/admin/enterprise/consumption/${cid}/${verb}`, {
      method: 'POST', headers, body: JSON.stringify(body),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); toast.error(e.detail || 'Erreur'); return; }
    toast.success(`Consommation ${verb === 'validate' ? 'validée' : verb === 'dispute' ? 'contestée' : 'annulée'}`);
    fetchAll();
  };

  const invoiceAction = async (invId, verb, body = null) => {
    const res = await fetch(`${API}/admin/enterprise/invoices/${invId}/${verb}`, {
      method: 'POST', headers, body: body ? JSON.stringify(body) : '{}',
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); toast.error(e.detail || 'Erreur'); return; }
    toast.success('OK'); fetchAll();
  };

  const downloadInvoicePdf = async (inv) => {
    const res = await fetch(`${API}/admin/enterprise/invoices/${inv.id}/pdf`, { headers });
    if (!res.ok) { toast.error('PDF indisponible'); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${inv.invoice_number}.pdf`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  if (loading) return <div className="text-sm text-[#6B7280] p-4"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Chargement reporting…</div>;
  if (!report) return null;
  const syn = report.synthesis;

  return (
    <motion.section initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-[#E8E5DE] rounded-2xl p-5" data-testid="admin-order-consumption">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-sm font-display font-medium text-[#111827] flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#92710A]" /> Consommation & Facturation
        </h2>
        <div className="flex gap-2">
          <button data-testid="admin-export-excel" onClick={() => download('excel')}
            className="px-3 py-1.5 rounded-full bg-[#F8F7F4] border border-[#E8E5DE] text-[#4B5563] text-xs font-medium hover:bg-[#EBEAE3] inline-flex items-center gap-1.5">
            <Download className="w-3.5 h-3.5" /> Excel
          </button>
          <button data-testid="admin-export-pdf" onClick={() => download('pdf')}
            className="px-3 py-1.5 rounded-full bg-[#F8F7F4] border border-[#E8E5DE] text-[#4B5563] text-xs font-medium hover:bg-[#EBEAE3] inline-flex items-center gap-1.5">
            <Printer className="w-3.5 h-3.5" /> PDF
          </button>
        </div>
      </div>

      {/* KPIs reporting */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <KPI label="Validées" value={syn.consumption_validated} accent="#047857" icon={CheckCircle2} />
        <KPI label="En attente" value={syn.consumption_pending} accent="#92400E" icon={Clock} />
        <KPI label="Contestées" value={syn.consumption_disputed} accent="#991B1B" icon={AlertTriangle} />
        <KPI label="Budget consommé" value={`${(syn.budget_consumed_ht || 0).toLocaleString('fr-FR')} €`} accent="#1E40AF" icon={Euro} />
        <KPI label="Crédits coach HT" value={`${(syn.coach_credited_total_ht || 0).toLocaleString('fr-FR')} €`} accent="#92710A" icon={Euro} />
        <KPI label="Revenu plateforme HT" value={`${(syn.platform_revenue_total_ht || 0).toLocaleString('fr-FR')} €`} accent="#047857" icon={Euro} />
        <KPI label="Budget engagé HT" value={`${(syn.budget_engaged_ht || 0).toLocaleString('fr-FR')} €`} accent="#6B7280" icon={Euro} />
        <KPI label="Progression" value={`${syn.progression_pct || 0}%`} accent="#111827" icon={Activity} />
      </div>

      {/* CONSUMPTION LIST */}
      <div className="mb-5">
        <h3 className="text-xs uppercase tracking-wider text-[#92710A] font-medium mb-2">
          Consommations récentes ({consumptions.length})
        </h3>
        {consumptions.length === 0 ? (
          <div className="text-sm text-[#6B7280] py-5 text-center bg-[#FBF9F3] border border-[#E8DFC8] rounded-xl">
            Aucune session consommée pour l'instant.
          </div>
        ) : (
          <div className="overflow-x-auto border border-[#E8DFC8] rounded-xl bg-white">
            <table className="w-full text-[12px]" data-testid="admin-consumption-table">
              <thead className="bg-[#F8F7F4] text-[#6B7280]">
                <tr>
                  <th className="p-2 text-left">Date</th>
                  <th className="p-2 text-left">Collaborateur</th>
                  <th className="p-2 text-left">Coach</th>
                  <th className="p-2 text-right">Client HT</th>
                  <th className="p-2 text-right">Coach Net HT</th>
                  <th className="p-2 text-center">Statut</th>
                  <th className="p-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {consumptions.slice(0, 50).map(c => {
                  const m = CONS_STATUS_META[c.status] || {};
                  return (
                    <tr key={c.id} className="border-t border-[#F0EEE8] hover:bg-[#FBF9F3]">
                      <td className="p-2 text-[#6B7280]">{(c.session_date || c.created_at || '').slice(0, 10)}</td>
                      <td className="p-2">{c.collaborator_email}</td>
                      <td className="p-2 text-[#6B7280] font-mono text-[10px]">{c.coach_user_id?.slice(0, 12)}…</td>
                      <td className="p-2 text-right font-semibold text-[#111827]" data-testid={`cons-client-ht-${c.id}`}>
                        {c.gross_ht?.toFixed(2)} €
                      </td>
                      <td className="p-2 text-right text-[#6B7280]" data-testid={`cons-coach-net-${c.id}`}>
                        {c.coach_net_ht?.toFixed(2)} €
                      </td>
                      <td className="p-2 text-center">
                        <span className="inline-flex text-[10px] uppercase font-medium px-2 py-0.5 rounded-full"
                          style={{ color: m.color, backgroundColor: m.bg }}>
                          {m.label}
                        </span>
                      </td>
                      <td className="p-2 text-right">
                        {c.status === 'pending_validation' && !c.validation_deadline && (
                          // 🔒 Iter248 — Séance pas encore consommée (deadline non armée) :
                          // aucune action manuelle. L'admin doit attendre report_generated
                          // OU J+1 fallback pour que la validation auto s'enclenche.
                          <div className="inline-flex items-center gap-1.5 text-[10px] text-[#6B7280]"
                               data-testid={`cons-awaiting-consume-${c.id}`}
                               title="La validation se fera automatiquement après consommation réelle (report généré ou J+1)">
                            <Hourglass className="w-3 h-3" />
                            <span>En attente séance</span>
                          </div>
                        )}
                        {c.status === 'pending_validation' && c.validation_deadline && (
                          <div className="inline-flex flex-col items-end gap-1" data-testid={`cons-auto-countdown-${c.id}`}>
                            <AutoValidationBadge deadlineIso={c.validation_deadline} />
                            <details className="text-[9px] text-[#9CA3AF]">
                              <summary className="cursor-pointer hover:text-[#111827] select-none">Override admin</summary>
                              <div className="inline-flex gap-1 mt-1">
                                <button data-testid={`cons-validate-${c.id}`}
                                  onClick={() => action(c.id, 'validate')}
                                  className="px-2 py-0.5 rounded-full bg-[#059669] text-white text-[10px] hover:bg-[#047857]">
                                  Valider
                                </button>
                                <button
                                  onClick={() => {
                                    const reason = window.prompt('Motif de contestation :');
                                    if (reason && reason.length >= 3) action(c.id, 'dispute', { reason });
                                  }}
                                  className="px-2 py-0.5 rounded-full bg-[#DC2626] text-white text-[10px] hover:bg-[#991B1B]">
                                  Contester
                                </button>
                              </div>
                            </details>
                          </div>
                        )}
                        {c.status === 'disputed' && (
                          <div className="inline-flex gap-1">
                            <button data-testid={`cons-validate-after-dispute-${c.id}`}
                              onClick={() => action(c.id, 'validate')}
                              className="px-2 py-0.5 rounded-full bg-[#059669] text-white text-[10px]">
                              Arbitrer → Valider
                            </button>
                            <button
                              onClick={() => {
                                const reason = window.prompt('Motif d\'annulation :');
                                if (reason && reason.length >= 3) action(c.id, 'cancel', { reason, restore_quota: true });
                              }}
                              className="px-2 py-0.5 rounded-full bg-[#6B7280] text-white text-[10px]">
                              Arbitrer → Annuler
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* INVOICES */}
      <div>
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h3 className="text-xs uppercase tracking-wider text-[#92710A] font-medium">
            Factures ({invoices.length}) · Facturation incrémentale
          </h3>
          <button data-testid="admin-invoice-create-btn"
            onClick={() => setShowInvoiceForm(s => !s)}
            className="px-3 py-1.5 rounded-full bg-[#111827] text-white text-xs font-medium hover:bg-[#1f2937] inline-flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Nouvelle facture
          </button>
        </div>

        {/* Synthèse delta — toujours affichée (facturation incrémentale unifiée) */}
        {invoicingSummary && (
          <div className="mb-3 p-3 rounded-xl bg-[#FBF9F3] border border-[#E8DFC8] grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]"
            data-testid="invoicing-delta-summary">
            <KPI label="Consommé total HT"
              value={`${(invoicingSummary.amount_total_ht || 0).toLocaleString('fr-FR')} €`}
              accent="#1E40AF" icon={Euro} />
            <KPI label="Déjà facturé HT"
              value={`${(invoicingSummary.amount_already_billed_ht || 0).toLocaleString('fr-FR')} €`}
              accent="#047857" icon={CheckCircle2} />
            <KPI label="Reste à facturer HT"
              value={`${(invoicingSummary.amount_remaining_to_bill_ht || 0).toLocaleString('fr-FR')} €`}
              accent="#92710A" icon={Clock} />
            <KPI label="Sessions à facturer"
              value={invoicingSummary.sessions_remaining_to_bill || 0}
              accent="#111827" icon={Activity} />
          </div>
        )}

        {showInvoiceForm && (
          <InvoiceCreateForm order={order} token={token}
            onDone={() => { setShowInvoiceForm(false); fetchAll(); }} />
        )}

        {invoices.length === 0 ? (
          <div className="text-sm text-[#6B7280] py-5 text-center bg-[#FBF9F3] border border-[#E8DFC8] rounded-xl">
            Aucune facture générée.
          </div>
        ) : (
          <div className="overflow-x-auto border border-[#E8DFC8] rounded-xl bg-white">
            <table className="w-full text-[12px]" data-testid="admin-invoices-table">
              <thead className="bg-[#F8F7F4] text-[#6B7280]">
                <tr>
                  <th className="p-2 text-left">N°</th>
                  <th className="p-2 text-left">Mode</th>
                  <th className="p-2 text-left">Période</th>
                  <th className="p-2 text-right">Total TTC</th>
                  <th className="p-2 text-center">Statut</th>
                  <th className="p-2 text-left">Échéance</th>
                  <th className="p-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => {
                  const m = INV_STATUS_META[inv.status] || {};
                  return (
                    <tr key={inv.id} className="border-t border-[#F0EEE8]">
                      <td className="p-2 font-mono font-medium text-[#111827]">{inv.invoice_number}</td>
                      <td className="p-2 text-[#6B7280] capitalize">{inv.mode}</td>
                      <td className="p-2 text-[#6B7280]">
                        {inv.period_start ? `${inv.period_start.slice(0, 10)} → ${inv.period_end.slice(0, 10)}` : '—'}
                      </td>
                      <td className="p-2 text-right font-medium">{inv.total_ttc.toLocaleString('fr-FR')} €</td>
                      <td className="p-2 text-center">
                        <span className="inline-flex text-[10px] uppercase font-medium px-2 py-0.5 rounded-full"
                          style={{ color: m.color, backgroundColor: m.bg }}>
                          {m.label}
                        </span>
                      </td>
                      <td className="p-2 text-[#6B7280]">{(inv.due_date || '').slice(0, 10)}</td>
                      <td className="p-2 text-right">
                        <div className="inline-flex gap-1 flex-wrap justify-end">
                          <button data-testid={`inv-pdf-${inv.id}`}
                            onClick={() => downloadInvoicePdf(inv)}
                            title="Télécharger PDF"
                            className="px-2 py-0.5 rounded-full bg-[#FBF9F3] border border-[#E8DFC8] text-[#4B5563] text-[10px] inline-flex items-center gap-1 hover:bg-[#EBEAE3]">
                            <Download className="w-3 h-3" /> PDF
                          </button>
                          {(inv.status === 'issued' || inv.status === 'overdue' || inv.status === 'paid') && (
                            <button data-testid={`inv-send-${inv.id}`}
                              onClick={() => setSendModalInv(inv)}
                              title="Envoyer par email"
                              className="px-2 py-0.5 rounded-full bg-[#F0F9FF] border border-[#BAE6FD] text-[#1E40AF] text-[10px] inline-flex items-center gap-1 hover:bg-[#E0F2FE]">
                              <Send className="w-3 h-3" /> Envoyer
                            </button>
                          )}
                          {inv.status === 'draft' && (
                            <button data-testid={`inv-issue-${inv.id}`}
                              onClick={() => invoiceAction(inv.id, 'issue')}
                              className="px-2 py-0.5 rounded-full bg-[#1E40AF] text-white text-[10px]">
                              Émettre
                            </button>
                          )}
                          {(inv.status === 'issued' || inv.status === 'overdue') && (
                            <button data-testid={`inv-mark-paid-${inv.id}`}
                              onClick={() => {
                                const ref = window.prompt('Référence paiement (virement, chèque…) :') || '';
                                invoiceAction(inv.id, 'mark-paid', { payment_reference: ref });
                              }}
                              className="px-2 py-0.5 rounded-full bg-[#059669] text-white text-[10px]">
                              Payée
                            </button>
                          )}
                          {inv.status !== 'paid' && inv.status !== 'void' && (
                            <button onClick={() => {
                              const reason = window.prompt('Motif d\'annulation :');
                              if (reason) invoiceAction(inv.id, 'void', { reason });
                            }}
                              className="px-2 py-0.5 rounded-full bg-[#6B7280] text-white text-[10px] inline-flex items-center gap-1">
                              <Ban className="w-3 h-3" /> Annuler
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Send modal */}
      {sendModalInv && (
        <InvoiceSendModal invoice={sendModalInv} token={token}
          defaultEmails={[order.company_snapshot?.hr_contact_email].filter(Boolean)}
          onClose={() => setSendModalInv(null)}
          onSent={() => { setSendModalInv(null); fetchAll(); }} />
      )}
    </motion.section>
  );
}

function InvoiceSendModal({ invoice, token, onClose, onSent, defaultEmails = [] }) {
  const [emails, setEmails] = useState(defaultEmails.length ? defaultEmails : ['']);
  const [subject, setSubject] = useState(`Facture ${invoice.invoice_number} — Coach DIA`);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const clean = emails.map(e => (e || '').trim()).filter(Boolean);
    if (clean.length === 0) { toast.error('Ajoutez au moins un destinataire'); return; }
    setSending(true);
    try {
      const res = await fetch(`${API}/admin/enterprise/invoices/${invoice.id}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ to: clean, subject, message: message || null }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Extraire message propre du detail (string OU objet structuré)
        const det = data.detail;
        const msg = typeof det === 'string'
          ? det
          : (det?.message || `Envoi SMTP échoué (${res.status})`);
        throw new Error(msg);
      }
      toast.success(`Facture envoyée à ${clean.length} destinataire(s) ✉️`);
      onSent();
    } catch (err) { toast.error(err.message); } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3"
      data-testid="invoice-send-modal" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl max-w-md w-full max-h-[95vh] overflow-y-auto p-5 relative">
        {/* Bouton X explicite, grand, toujours accessible */}
        <button onClick={onClose}
          aria-label="Fermer" data-testid="invoice-send-close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-[#F3F4F6] hover:bg-[#E5E7EB] inline-flex items-center justify-center z-10">
          <X className="w-5 h-5 text-[#111827]" />
        </button>
        <h3 className="text-lg font-display font-medium text-[#111827] flex items-center gap-2 pr-10">
          <Mail className="w-5 h-5 text-[#92710A]" /> Envoyer la facture {invoice.invoice_number}
        </h3>
        <p className="text-xs text-[#6B7280] mt-1">
          Destinataires multiples : un email par champ. Le PDF sera joint automatiquement.
        </p>
        <form onSubmit={submit} className="mt-3 space-y-2">
          <div className="space-y-1">
            <label className="text-[11px] uppercase tracking-wider text-[#92710A] font-medium">
              Emails destinataires *
            </label>
            {emails.map((em, i) => (
              <div key={i} className="flex gap-1">
                <input type="email" required placeholder="destinataire@entreprise.com"
                  value={em}
                  onChange={(e) => setEmails(emails.map((x, k) => k === i ? e.target.value : x))}
                  data-testid={`send-email-${i}`}
                  className="flex-1 p-2 rounded-lg border border-[#E8E5DE] text-sm focus:outline-none focus:border-[#92710A]" />
                {emails.length > 1 && (
                  <button type="button"
                    onClick={() => setEmails(emails.filter((_, k) => k !== i))}
                    className="px-2 text-[#991B1B] hover:bg-[#FEE2E2] rounded-lg">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            {emails.length < 10 && (
              <button type="button" onClick={() => setEmails([...emails, ''])}
                data-testid="send-add-email"
                className="text-[11px] text-[#92710A] hover:underline">
                + Ajouter un destinataire
              </button>
            )}
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#92710A] font-medium">Objet</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)}
              data-testid="send-subject"
              className="w-full p-2 rounded-lg border border-[#E8E5DE] text-sm focus:outline-none focus:border-[#92710A]" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#92710A] font-medium">
              Message (optionnel)
            </label>
            <textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder="Un mot d'accompagnement…" data-testid="send-message"
              className="w-full p-2 rounded-lg border border-[#E8E5DE] text-sm focus:outline-none focus:border-[#92710A]" />
          </div>
          <button type="submit" disabled={sending}
            data-testid="send-submit"
            className="w-full px-4 py-2.5 rounded-lg bg-[#111827] text-white text-sm font-medium hover:bg-[#1f2937] disabled:opacity-50 inline-flex items-center justify-center gap-2">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Envoyer la facture
          </button>
        </form>
      </div>
    </div>
  );
}

function KPI({ label, value, accent = '#111827', icon: Icon }) {
  return (
    <div className="p-3 rounded-xl bg-[#FBF9F3] border border-[#E8DFC8]">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-medium">{label}</span>
        {Icon && <Icon className="w-3.5 h-3.5" style={{ color: accent }} />}
      </div>
      <div className="text-lg font-display mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function InvoiceCreateForm({ order, token, onDone }) {
  // Flux unifié : TOUJOURS une facture DELTA sur une période
  // (facturation incrémentale, historique conservé, aucun doublon possible).
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  const toISO = (d) => d.toISOString().slice(0, 10);
  const [periodStart, setPeriodStart] = useState(toISO(firstDay));
  const [periodEnd, setPeriodEnd] = useState(toISO(today));
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/enterprise/invoices/consumption`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          order_id: order.id,
          period_start: `${periodStart}T00:00:00+00:00`,
          period_end: `${periodEnd}T23:59:59+00:00`,
          notes: notes || null,
        }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        const rawDetail = typeof e.detail === 'string' ? e.detail : JSON.stringify(e.detail || '');
        const friendly = {
          no_unbilled_consumptions_in_period: 'Aucune session validée à facturer sur cette période (toutes déjà facturées ou aucune conso validée).',
          no_validated_consumptions_in_period: 'Aucune session validée sur cette période.',
        }[rawDetail] || rawDetail || 'Erreur';
        throw new Error(friendly);
      }
      toast.success('Nouvelle facture incrémentale créée');
      onDone();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit}
      className="mb-3 p-4 rounded-xl bg-[#FBF9F3] border border-[#E8DFC8]"
      data-testid="admin-invoice-form">
      <div className="text-xs text-[#4B5563] mb-3 flex items-start gap-2">
        <Receipt className="w-4 h-4 text-[#92710A] flex-shrink-0 mt-0.5" />
        <div>
          <b className="text-[#111827]">Facturation incrémentale (delta)</b> — Cette facture couvre
          uniquement les sessions <b>validées sur la période</b> et <b>non encore rattachées à une
          facture antérieure non-annulée</b>. Vous pouvez générer autant de factures que nécessaire :
          mensuelles, trimestrielles ou sur demande. Aucune session n'est jamais facturée deux fois.
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#92710A] font-medium block mb-1">
            Début de période
          </label>
          <input type="date" required value={periodStart} onChange={e => setPeriodStart(e.target.value)}
            data-testid="invoice-period-start"
            className="w-full p-2 rounded-lg bg-white border border-[#E8E5DE] text-sm" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#92710A] font-medium block mb-1">
            Fin de période
          </label>
          <input type="date" required value={periodEnd} onChange={e => setPeriodEnd(e.target.value)}
            data-testid="invoice-period-end"
            className="w-full p-2 rounded-lg bg-white border border-[#E8E5DE] text-sm" />
        </div>
      </div>
      <textarea placeholder="Notes internes (optionnel)" value={notes} onChange={e => setNotes(e.target.value)}
        data-testid="invoice-notes"
        className="w-full p-2 rounded-lg bg-white border border-[#E8E5DE] text-sm mb-2" rows={2} />
      <button type="submit" disabled={saving}
        data-testid="invoice-submit"
        className="w-full px-4 py-2.5 rounded-lg bg-[#111827] text-white text-sm font-semibold hover:bg-[#1f2937] disabled:opacity-50 inline-flex items-center justify-center gap-1.5">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
        Générer la facture incrémentale
      </button>
    </form>
  );
}
